/**
 * Hook för EnergyConsumptionReport
 * Hanterar state och logik för energiförbrukningsrapport
 */

import { useState, useEffect, useCallback } from 'react';
import api from '@/services/api/client';
import {
  EnergyStats,
  OrganizationEnergyStats,
  EnergySummary,
  TrendData,
  DateRange,
  EnergyTab,
  getDefaultDateRange,
} from '@/types/energyConsumption';

export function useEnergyConsumption() {
  // UI state
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<EnergyTab>('overview');

  // Filter state
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange());

  // Data state
  const [summary, setSummary] = useState<EnergySummary | null>(null);
  const [orgStats, setOrgStats] = useState<OrganizationEnergyStats[]>([]);
  const [vehicleStats, setVehicleStats] = useState<EnergyStats[]>([]);
  const [trendData, setTrendData] = useState<TrendData[]>([]);

  // Fetch data when org or date changes
  const fetchData = useCallback(async () => {
    if (!selectedOrg) return;

    setLoading(true);
    try {
      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        organizationId: selectedOrg,
      };

      const [summaryRes, vehiclesRes, trendRes] = await Promise.all([
        api.get('/api/reports/energy-consumption', { params }),
        api.get('/api/reports/energy-consumption/vehicles', { params }),
        api.get('/api/reports/energy-consumption/trend', {
          params: { ...params, groupBy: 'day' },
        }),
      ]);

      if (summaryRes.data.success) {
        setSummary(summaryRes.data.data.summary);
        setOrgStats(summaryRes.data.data.organizations);
      }

      if (vehiclesRes.data.success) {
        setVehicleStats(vehiclesRes.data.data);
      }

      if (trendRes.data.success) {
        setTrendData(trendRes.data.data);
      }
    } catch {
      // Error fetching energy data - handled silently
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, dateRange]);

  useEffect(() => {
    if (selectedOrg) {
      fetchData();
    }
  }, [selectedOrg, dateRange, fetchData]);

  // Export report
  const handleExport = useCallback(async () => {
    try {
      const response = await api.get('/api/reports/energy-consumption/export', {
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          organizationId: selectedOrg,
          format: 'csv',
        },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `energy_report_${dateRange.startDate}_${dateRange.endDate}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      // Export error - handled silently
    }
  }, [dateRange, selectedOrg]);

  // Update date range
  const updateDateRange = useCallback(
    (field: keyof DateRange, value: string) => {
      setDateRange((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  return {
    // UI state
    loading,
    activeTab,
    setActiveTab,

    // Filter state
    selectedOrg,
    setSelectedOrg,
    dateRange,
    updateDateRange,

    // Data state
    summary,
    orgStats,
    vehicleStats,
    trendData,

    // Actions
    handleExport,
  };
}
